import { useRef } from 'react'
import { cn } from '#app/utils/misc.tsx'

/**
 * Photo pieces (plan §1.7 OyPhotoGrid / OyPhotoPicker / OyPhotoViewer).
 * Tiles are served through the viewer-gated /resources/photo/:id route
 * (never /resources/images); the picker is a real file input behind the
 * dashed "Kies foto's" button with numbered, removable previews.
 */

export type OyPhotoTileData = {
	id: string
	src: string
	label?: string | null
	alt?: string
}

export function photoSrc(id: string) {
	return `/resources/photo/${id}`
}

export function OyPhotoGrid({
	photos,
	onOpen,
	tight,
	placeholders = 0,
	className,
}: {
	photos: OyPhotoTileData[]
	onOpen?: (id: string) => void
	tight?: boolean
	/** empty dashed cells to keep the row (Vandaag's three tiles) */
	placeholders?: number
	className?: string
}) {
	const empties = Math.max(0, placeholders - photos.length)
	return (
		<div className={cn('st-phototiles', tight && 'tight', className)}>
			{photos.map((p) => (
				<OyPhotoTile key={p.id} photo={p} onOpen={onOpen} />
			))}
			{Array.from({ length: empties }, (_, i) => (
				<div key={`e${i}`} className="st-tile-empty" aria-hidden="true">
					{i === 0 && photos.length === 0 ? 'nog geen foto' : ''}
				</div>
			))}
		</div>
	)
}

export function OyPhotoTile({
	photo,
	onOpen,
	badge,
	onRemove,
}: {
	photo: OyPhotoTileData
	onOpen?: (id: string) => void
	/** the picker's number */
	badge?: number
	onRemove?: () => void
}) {
	const body = (
		<>
			<span className="img">
				<img
					src={photo.src}
					alt={photo.alt ?? photo.label ?? 'foto'}
					loading="lazy"
				/>
				{photo.label ? <span className="cap">{photo.label}</span> : null}
			</span>
			{badge != null ? (
				<span className="n" aria-hidden="true">
					{badge}
				</span>
			) : null}
		</>
	)
	if (onRemove) {
		return (
			<button
				type="button"
				className="st-tile"
				aria-label={`Foto ${badge ?? ''} weghalen`}
				onClick={onRemove}
			>
				{body}
			</button>
		)
	}
	if (onOpen) {
		return (
			<button
				type="button"
				className="st-tile"
				aria-label={photo.label ? `Bekijk: ${photo.label}` : 'Bekijk foto'}
				onClick={() => onOpen(photo.id)}
			>
				{body}
			</button>
		)
	}
	return <span className="st-tile">{body}</span>
}

export type PickedPhoto = { key: string; file: File; src: string }

/**
 * The picker inside the foto tray: dashed 150px button when empty, then
 * numbered tiles (tap = remove) and a "Meer" cell. Files come from a real
 * <input type=file multiple accept=image/*>; previews are object URLs the
 * caller revokes when the tray closes.
 */
export function OyPhotoPicker({
	picked,
	onPick,
	onRemove,
	disabled,
}: {
	picked: PickedPhoto[]
	onPick: (files: File[]) => void
	onRemove: (key: string) => void
	disabled?: boolean
}) {
	const inputRef = useRef<HTMLInputElement>(null)
	const input = (
		<input
			ref={inputRef}
			type="file"
			accept="image/*"
			multiple
			className="sr-only"
			tabIndex={-1}
			aria-hidden="true"
			onChange={(e) => {
				const files = Array.from(e.target.files ?? [])
				if (files.length) onPick(files)
				e.target.value = ''
			}}
		/>
	)
	if (picked.length === 0) {
		return (
			<>
				{input}
				<button
					type="button"
					className="st-pick"
					disabled={disabled}
					onClick={() => inputRef.current?.click()}
				>
					📷 Kies foto's
				</button>
			</>
		)
	}
	return (
		<>
			{input}
			<div className="st-phototiles">
				{picked.map((p, i) => (
					<OyPhotoTile
						key={p.key}
						photo={{
							id: p.key,
							src: p.src,
							label: p.file.name.replace(/\.[^.]+$/, ''),
						}}
						badge={i + 1}
						onRemove={disabled ? undefined : () => onRemove(p.key)}
					/>
				))}
				<button
					type="button"
					className="st-pick more"
					disabled={disabled}
					onClick={() => inputRef.current?.click()}
				>
					📷<span>Meer</span>
				</button>
			</div>
		</>
	)
}

export function OyPhotoViewer({
	src,
	alt,
	meta,
}: {
	src: string
	alt: string
	meta: string[]
}) {
	return (
		<>
			<div className="st-viewer">
				<img src={src} alt={alt} />
			</div>
			<div className="st-metachips">
				{meta.map((m) => (
					<span key={m} className="oy-pill oy-pill-muted">
						{m}
					</span>
				))}
			</div>
		</>
	)
}
